import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/config/firebase";
import type {
  AppMetrics,
  AppMetricsTimelinePoint,
} from "@/types/appMetrics.types";

const APP_METRICS = "appMetrics";

/**
 * Latest aggregated snapshot, written by Cloud Function aggregateDailyAppMetrics.
 * Falls back to the most recent dated doc if no `latest` pointer exists yet.
 */
export async function getLatestAppMetrics(): Promise<AppMetrics | null> {
  const latestRef = doc(db, APP_METRICS, "latest");
  const latestSnap = await getDoc(latestRef);
  if (latestSnap.exists()) {
    return latestSnap.data() as AppMetrics;
  }

  const q = query(
    collection(db, APP_METRICS),
    orderBy("date", "desc"),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  if (docSnap.id === "latest") return null;
  return docSnap.data() as AppMetrics;
}

/**
 * Returns the last N days as timeline points for charts.
 * Skips the `latest` pointer doc.
 */
export async function getAppMetricsTimeline(
  days = 30,
): Promise<AppMetricsTimelinePoint[]> {
  const q = query(
    collection(db, APP_METRICS),
    orderBy("date", "desc"),
    limit(days + 5),
  );
  const snap = await getDocs(q);
  const points: AppMetricsTimelinePoint[] = [];
  snap.docs.forEach((d) => {
    if (d.id === "latest") return;
    const data = d.data() as AppMetrics;
    points.push({
      date: data.date,
      dau: data.dau ?? 0,
      mau: data.mau ?? 0,
      newUsers: data.newUsersToday ?? 0,
      mrr: data.revenue?.mrr ?? 0,
    });
  });
  return points.sort((a, b) => a.date.localeCompare(b.date)).slice(-days);
}

/**
 * Triggers on-demand aggregation (admin-only callable Cloud Function).
 */
export async function refreshAppMetrics(): Promise<{ date: string }> {
  // v1 callable functions default to us-central1 unless region is set explicitly
  // in the Cloud Function. The other admin callables use the same region.
  const fns = getFunctions(getApp(), "us-central1");
  const fn = httpsCallable<unknown, { ok: boolean; date: string }>(
    fns,
    "refreshAppMetricsNow",
  );
  const result = await fn({});
  return { date: result.data.date };
}
